import { AiBubble, UserBubble } from "@/components/chat/ChatBubble";
import { SummaryProgressIndicator, ThinkingIndicator } from "@/components/onboarding/ThinkingIndicator";
import type { TMessage } from "@/types/chat";
import { format, isValid, parseISO } from "date-fns";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";

const MessageTimestamp = ({ value, align }: { value?: string; align: "start" | "end" }) => {
  if (!value) return null;
  const date = parseISO(value);
  if (!isValid(date)) return null;
  return (
    <div className={`mt-1 text-xs text-muted-foreground ${align === "end" ? "text-right" : "text-left"}`}>
      {format(date, "MMM d, yyyy · HH:mm")}
    </div>
  );
};

// Parse JSON from markdown code blocks
const parseJsonFromMarkdown = (content: string): { response?: string; updated_summary?: string } | null => {
  if (!content || typeof content !== "string") {
    return null;
  }

  // Check if content is JSON wrapped in markdown code blocks
  const jsonCodeBlockRegex = /```json\s*([\s\S]*?)\s*```/;
  const match = jsonCodeBlockRegex.exec(content);

  if (!match?.[1]) {
    return null;
  }

  try {
    const jsonStr = match[1].trim();
    const parsed = JSON.parse(jsonStr) as { response?: string; updated_summary?: string };
    return parsed;
  } catch {
    // If parsing fails, return null to fall back to regular markdown rendering
    return null;
  }
};

// Safe markdown renderer component with JSON formatting
const SafeMarkdown = ({ content }: { content: string }) => {
  if (!content || typeof content !== "string") {
    return <span>{String(content || "")}</span>;
  }

  // Try to parse JSON from markdown code blocks
  const jsonData = parseJsonFromMarkdown(content);

  if (jsonData) {
    // Format JSON response nicely
    return (
      <div className="json-response">
        {jsonData.response && (
          <div className="mb-4">
            <div className="whitespace-pre-wrap text-foreground">{jsonData.response}</div>
          </div>
        )}
        {jsonData.updated_summary?.trim() && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="mb-2 text-sm font-medium text-foreground">Updated Summary:</div>
            <div className="whitespace-pre-wrap text-muted-foreground">{jsonData.updated_summary}</div>
          </div>
        )}
      </div>
    );
  }

  // Fall back to regular markdown rendering
  return <ReactMarkdown>{content}</ReactMarkdown>;
};

type TMessageListVariant = "stack" | "split";

type TMessageListProps = {
  messages: TMessage[];
  variant?: TMessageListVariant;
};

export default function MessageList({ messages, variant = "stack" }: TMessageListProps) {
  const isSplit = variant === "split";

  const containerClassName = useMemo(() => {
    return "p-4 md:p-6 min-h-full";
  }, []);

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-4 text-muted-foreground">
        <p>No messages yet. Start the conversation!</p>
      </div>
    );
  }

  const renderFileLink = (file: { id: string; name: string; url?: string }) => {
    const handleFileClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (file.url && file.url !== "#") {
        window.open(file.url, "_blank", "noopener,noreferrer");
      }
    };

    return (
      <div className="mt-2">
        <span className="text-sm italic text-primary">
          (Uploaded file:{" "}
          {file.url && file.url !== "#" ? (
            <button
              onClick={handleFileClick}
              className="underline transition-colors hover:text-primary/80"
            >
              &quot;{file.name}&quot;
            </button>
          ) : (
            <span>&quot;{file.name}&quot;</span>
          )}
          )
        </span>
      </div>
    );
  };

  /** Bubble body for an AI message. Text and indicator are additive, never either/or. */
  const renderAiContent = (message: TMessage) => {
    const hasBody = Boolean(message.text) || Boolean(message.files?.length);

    return (
      <>
        {message.text && (
          <div className="markdown-content">
            <SafeMarkdown content={message.text} />
          </div>
        )}

        {!hasBody && !message.thinking && message.kind !== "progress" && (
          <p className="text-sm italic text-muted-foreground">No response received.</p>
        )}

        {(message.thinking === true || message.kind === "progress") && (
          <div className={message.text ? "mt-3" : undefined}>
            {message.kind === "progress" ? (
              <SummaryProgressIndicator
                stage={message.progressStage}
                elapsedSeconds={message.progressElapsedSeconds}
              />
            ) : (
              <ThinkingIndicator />
            )}
          </div>
        )}

        {message.helperText && (
          <div className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{message.helperText}</div>
        )}

        {message.files && message.files.length > 0 && (
          <div className="mt-2">
            {message.files.map((file) => (
              <div key={file.id}>{renderFileLink(file)}</div>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderUserContent = (message: TMessage) => (
    <>
      <div className="markdown-content">
        <SafeMarkdown content={message.text ?? ""} />
      </div>

      {message.helperText && (
        <div className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{message.helperText}</div>
      )}

      {message.files && message.files.length > 0 && (
        <div className="mt-2">
          {message.files.map((file) => (
            <div key={file.id}>{renderFileLink(file)}</div>
          ))}
        </div>
      )}
    </>
  );

  const renderErrorBubble = (message: TMessage) => {
    const isThinking = messages.some((m) => m.thinking);
    return (
      <div
        key={`${message.id}-error`}
        className="mb-6 flex justify-start"
      >
        <div className="flex w-full items-center gap-2 px-6 py-3">
          <span className="flex-none text-amber-600 dark:text-amber-400">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              aria-hidden="true"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
              />
              <line
                x1="12"
                y1="7"
                x2="12"
                y2="13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle
                cx="12"
                cy="16.3"
                r="1.1"
                fill="currentColor"
              />
            </svg>
          </span>
          <span className="text-xs text-muted-foreground">
            Couldn&apos;t generate a response.{" "}
            <button
              onClick={() => {
                const event = new CustomEvent("retry-message", { detail: { messageId: message.id } });
                window.dispatchEvent(event);
              }}
              disabled={isThinking}
              className="font-semibold text-primary underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isThinking ? "Trying again…" : "Try again"}
            </button>
          </span>
        </div>
      </div>
    );
  };

  const renderStackedMessages = () =>
    messages.flatMap((message) => {
      if (message.sender === "ai") {
        return [
          <div
            key={message.id}
            className="mb-6 flex justify-start"
          >
            <AiBubble
              kind={message.kind}
              footer={
                <MessageTimestamp
                  value={message.createdAt}
                  align="start"
                />
              }
            >
              {renderAiContent(message)}
            </AiBubble>
          </div>,
        ];
      }

      const userRow = (
        <div
          key={message.id}
          className="mb-4 flex justify-end"
        >
          <UserBubble
            footer={
              <MessageTimestamp
                value={message.createdAt}
                align="end"
              />
            }
          >
            {renderUserContent(message)}
          </UserBubble>
        </div>
      );

      return message.error ? [userRow, renderErrorBubble(message)] : [userRow];
    });

  const renderSplitMessages = () => {
    const pairs = messages.reduce<{ id: string; user?: TMessage; ai?: TMessage }[]>((acc, message) => {
      const lastPair = acc[acc.length - 1];
      if (message.sender === "user") {
        if (lastPair && !lastPair.user && lastPair.ai) {
          lastPair.user = message;
        } else {
          acc.push({ id: `${message.id}-pair`, user: message });
        }
      } else if (lastPair && !lastPair.ai) {
        lastPair.ai = message;
      } else {
        acc.push({ id: `${message.id}-pair`, ai: message });
      }
      return acc;
    }, []);

    const renderCard = (message: TMessage, role: "user" | "ai") => {
      if (role === "ai") {
        return (
          <AiBubble
            kind={message.kind}
            footer={
              <MessageTimestamp
                value={message.createdAt}
                align="start"
              />
            }
          >
            {renderAiContent(message)}
          </AiBubble>
        );
      }

      // User message
      return (
        <UserBubble
          footer={
            <MessageTimestamp
              value={message.createdAt}
              align="end"
            />
          }
        >
          {renderUserContent(message)}
        </UserBubble>
      );
    };


    return (
      <div>
        {pairs.map((pair) => (
          <div
            key={pair.id}
            className="mb-6"
          >
            {pair.user && <div className="mb-4 flex justify-end">{renderCard(pair.user, "user")}</div>}
            {pair.user?.error && !pair.ai && renderErrorBubble(pair.user)}
            {pair.ai && <div className="flex justify-start">{renderCard(pair.ai, "ai")}</div>}
          </div>
        ))}
      </div>
    );
  };

  return <div className={containerClassName}>{isSplit ? renderSplitMessages() : renderStackedMessages()}</div>;
}
